/**
 * Token do Cloudflare Turnstile para as rotas públicas que a API protege com
 * `CaptchaGuard` (cadastro, login, envio de código, agendamento pelo link).
 *
 * O `CaptchaHost` (montado no layout raiz) registra quem sabe gerar o token:
 * na web o widget no DOM; no nativo uma WebView. O interceptor do axios pede
 * um token novo a cada chamada protegida — ele é de uso único.
 *
 * Sem `EXPO_PUBLIC_TURNSTILE_SITE_KEY` nada acontece e a chamada sai sem o
 * header; a API também só exige quando tem a secret.
 */

export const TURNSTILE_SITE_KEY = process.env.EXPO_PUBLIC_TURNSTILE_SITE_KEY?.trim() ?? '';

/** Header que a API lê (`CAPTCHA_HEADER` no backend). */
export const CAPTCHA_HEADER = 'X-Captcha-Token';

/** Folga para o visitante resolver o desafio quando a Cloudflare pede clique. */
const TOKEN_TIMEOUT_MS = 60_000;

type Executor = () => Promise<string>;

let executor: Executor | null = null;
let queue: Promise<unknown> = Promise.resolve();

export function registerCaptchaExecutor(fn: Executor): () => void {
  executor = fn;
  return () => {
    if (executor === fn) executor = null;
  };
}

/** Espelha as rotas com `@UseGuards(CaptchaGuard)` na API. */
const PROTECTED_POSTS: RegExp[] = [
  /^\/auth\/?$/,
  /^\/auth\/register\/(provider|client)$/,
  /^\/auth\/phone\/request-code$/,
  /^\/auth\/forgot-password$/,
  /^\/public\/schedule\/[^/]+\/(otp\/request|appointments|recurrences|subscriptions)$/,
];

export function needsCaptcha(method: string | undefined, url: string | undefined): boolean {
  if (!TURNSTILE_SITE_KEY || !url) return false;
  if ((method ?? 'get').toLowerCase() !== 'post') return false;
  const path = url.replace(/^https?:\/\/[^/]+/, '').split('?')[0];
  return PROTECTED_POSTS.some((re) => re.test(path));
}

/**
 * Um token novo, ou `null` quando não deu (sem host, erro, tempo esgotado):
 * a chamada segue e a API responde com a mensagem de "não é robô".
 * Em fila: o widget é um só e executa um desafio por vez.
 */
export function getCaptchaToken(): Promise<string | null> {
  const run = queue.then(async () => {
    const current = executor;
    if (!TURNSTILE_SITE_KEY || !current) return null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        current(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error('captcha timeout')), TOKEN_TIMEOUT_MS);
        }),
      ]);
    } catch (error) {
      console.warn('[captcha] sem token:', error);
      return null;
    } finally {
      clearTimeout(timer);
    }
  });
  queue = run.catch(() => undefined);
  return run;
}
